/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from 'react';
import { IconButton } from "@/subframe/components/IconButton";
import { DialogLayout } from "@/subframe/layouts/DialogLayout";
import { TextArea } from "@/subframe/components/TextArea";
import { Tooltip } from "@/subframe/components/Tooltip";
import { Button } from "@/subframe/components/Button";
import SubframeCore from "@subframe/core";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../redux/store";
import { handleMediaSend } from "../../redux/reducers/mediaMessageSlice";
import ScheduleMessage from "../templates/ScheduleTemplateMenu";
import type { SchedulePayload } from "../templates/ScheduleTemplateMenu";
import { addNotification } from "../../redux/reducers/notificationSlice";
import { NotificationIcon, NotificationType } from "../../Utils/notificationEnums";
import moment from "moment";
import { addEsChatToConfig } from "../../Utils/apiUtils";

const MAX_FILES = 10;
const UPLOAD_CONCURRENCY = 3;

/** Normalize type so attachment audio (mp3, m4a, …) uses `audio` → `/web/send/audio` when API omits or mislabels. */
function resolveUploadedFileType(
  data: { file_type?: string; mime_type?: string },
  file: File
): string {
  const fromApi = (data?.file_type || "").toLowerCase();
  if (fromApi === "image" || fromApi === "video" || fromApi === "audio") {
    return fromApi;
  }
  const mime = (data?.mime_type || file.type || "").toLowerCase();
  if (mime.startsWith("audio/")) return "audio";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("image/")) return "image";
  if (/\.(mp3|m4a|aac|wav|ogg|opus|flac|caf|aiff?)$/i.test(file.name)) return "audio";
  if (/\.webm$/i.test(file.name) && mime === "audio/webm") return "audio";
  return fromApi || "document";
}

interface FileSelectionMenuProps {
  currentRecipient: any;
}

type UploadedMediaItem = {
  id: string;
  file_url: string;
  file_type: string;
  original_name: string;
  size?: number;
  fileType?: string;
};

const FileSelectionMenu: React.FC<FileSelectionMenuProps> = ({ currentRecipient }) => {
  const dispatch = useDispatch<AppDispatch>();
  const system_user_id = useSelector((state: RootState) => state.auth.system_user_id);
  const userDisplayName = useSelector((state: RootState) => state.auth.userDisplayName);
  const isMediaUploading = useSelector((state: RootState) => state.mediaMessage.isLoading);

  const [showPreview, setShowPreview] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedMediaItem[]>([]);
  const [selectedPreviewIndex, setSelectedPreviewIndex] = useState(0);
  const [caption, setCaption] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isSendingBatch, setIsSendingBatch] = useState(false);
  const [isScheduleMessagePopupOpen, setIsScheduleMessagePopupOpen] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadOneFile = async (
    file: File
  ): Promise<{ ok: true; data: any } | { ok: false; message: string }> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const config = addEsChatToConfig();

      const headers: Record<string, string> = {
        'Accept': '*/*'
      };

      if (config.headers?.Authorization) {
        headers.Authorization = config.headers.Authorization as string;
      }

      const response = await fetch('https://api.insightssystem.com/api:bVXsw_FD/upload_file_by_attachment', {
        method: 'POST',
        headers,
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Upload failed:', response.status, errorText);
        return { ok: false, message: `Upload failed: ${response.status} ${response.statusText}` };
      }

      const result = await response.json();

      if (result.success) {
        return { ok: true, data: result };
      }
      return { ok: false, message: result.error || result.message || 'Upload failed' };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Please try again.';
      console.error('Upload error:', error);
      return { ok: false, message: `Failed to upload file: ${message}` };
    }
  };

  const mapResultToItem = (data: any, file: File): UploadedMediaItem => ({
    id: typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `up-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    file_url: data.file_url,
    file_type: resolveUploadedFileType(data, file),
    original_name: data.original_name || file.name || 'file',
    size: typeof data.size === 'number' ? data.size : undefined,
    fileType: data.fileType || data.mime_type || file.type,
  });

  const uploadFilesBatch = async (files: File[]) => {
    if (files.length === 0) return;

    setIsUploading(true);
    setUploadError(null);
    setUploadProgress({ done: 0, total: files.length });
    setUploadedFiles([]);

    const successful: UploadedMediaItem[] = [];
    let processed = 0;

    for (let i = 0; i < files.length; i += UPLOAD_CONCURRENCY) {
      const batch = files.slice(i, i + UPLOAD_CONCURRENCY);
      const outcomes = await Promise.all(
        batch.map((file) => uploadOneFile(file).then((out) => ({ file, out })))
      );

      for (const { file, out } of outcomes) {
        processed += 1;
        setUploadProgress({ done: processed, total: files.length });
        if (out.ok) {
          successful.push(mapResultToItem(out.data, file));
        } else {
          dispatch(
            addNotification({
              id: Date.now() + Math.random(),
              type: NotificationType.ERROR,
              title: "Upload failed",
              message: `${file.name}: ${out.message}`,
              icon: NotificationIcon.ERROR,
            })
          );
        }
      }
    }

    setUploadProgress(null);
    setIsUploading(false);

    if (successful.length === 0) {
      setUploadError('No files uploaded successfully. Please try again.');
      return;
    }

    setUploadedFiles(successful);
    setSelectedPreviewIndex(0);
    setShowPreview(true);
  };

  const pickFilesFromList = (fileList: FileList | null): File[] => {
    if (!fileList?.length) return [];
    const raw = Array.from(fileList);
    if (raw.length > MAX_FILES) {
      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.ERROR,
          title: "Too many files",
          message: `Only the first ${MAX_FILES} files were selected.`,
          icon: NotificationIcon.ERROR,
        })
      );
    }
    return raw.slice(0, MAX_FILES);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = pickFilesFromList(e.target.files);
    if (files.length) {
      await uploadFilesBatch(files);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const files = pickFilesFromList(e.dataTransfer.files);
    if (files.length) {
      await uploadFilesBatch(files);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const removeUploadedFile = (id: string) => {
    setUploadedFiles((prev) => {
      const next = prev.filter((f) => f.id !== id);
      setSelectedPreviewIndex((idx) => {
        if (next.length === 0) return 0;
        if (idx >= next.length) return next.length - 1;
        return idx;
      });
      return next;
    });
  };

  useEffect(() => {
    if (showPreview && uploadedFiles.length === 0 && !isUploading) {
      setShowPreview(false);
    }
  }, [showPreview, uploadedFiles.length, isUploading]);

  useEffect(() => {
    if (selectedPreviewIndex >= uploadedFiles.length && uploadedFiles.length > 0) {
      setSelectedPreviewIndex(uploadedFiles.length - 1);
    }
  }, [uploadedFiles.length, selectedPreviewIndex]);

  const handleSend = async (schedulePayload?: SchedulePayload) => {
    if (uploadedFiles.length === 0 || !system_user_id || !currentRecipient) return;

    const captionSend = caption.trim();
    if (!captionSend) {
      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.ERROR,
          title: "Caption Required",
          message: "Please add a caption before sending",
          icon: NotificationIcon.ERROR,
        })
      );
      return;
    }

    const isScheduled = !!schedulePayload?.is_schedule;

    setIsSendingBatch(true);
    try {
      for (const item of uploadedFiles) {
        try {
          await dispatch(handleMediaSend({
            media_link: item.file_url,
            type: item.file_type,
            caption: captionSend,
            currentRecipient,
            senderUserId: system_user_id,
            senderDisplayName: userDisplayName,
            is_schedule: isScheduled,
            ...(schedulePayload || {}),
          })).unwrap();
        } catch (error: unknown) {
          const msg = error instanceof Error ? error.message : "Something went wrong. Please try again.";
          const errorTitle = isScheduled ? "Schedule Failed" : "Media Not Sent";
          dispatch(
            addNotification({
              id: Date.now(),
              type: NotificationType.ERROR,
              title: errorTitle,
              message: `${item.original_name}: ${msg}`,
              icon: NotificationIcon.ERROR,
            })
          );
          return;
        }
      }

      const count = uploadedFiles.length;

      if (isScheduled) {
        let scheduleMessage = "Your media has been scheduled.";

        if (schedulePayload?.schedule_type === "specific") {
          const formattedLocalDateTime = schedulePayload.schedule_datetime_date
            ? moment(schedulePayload.schedule_datetime_date).local().format("MMM D, YYYY hh:mm A")
            : "selected date and time";
          const selectedTimezone = schedulePayload.time_zone || "your timezone";
          scheduleMessage =
            count > 1
              ? `${count} files scheduled for ${formattedLocalDateTime} (${selectedTimezone}).`
              : `Your media will be sent on ${formattedLocalDateTime} (${selectedTimezone}).`;
        } else if (schedulePayload?.schedule_type === "relative") {
          const unitValue = schedulePayload.unit_of_time_value || "";
          const unitName = schedulePayload.unit_of_time_name || "";
          scheduleMessage =
            count > 1
              ? `${count} files scheduled after ${unitValue} ${unitName} from now.`
              : `Your media will be sent after ${unitValue} ${unitName} from now.`;
        }

        dispatch(
          addNotification({
            id: Date.now(),
            type: NotificationType.SUCCESS,
            title: "Media Scheduled",
            message: scheduleMessage,
            icon: NotificationIcon.SUCCESS,
          })
        );
      } else {
        dispatch(
          addNotification({
            id: Date.now(),
            type: NotificationType.SUCCESS,
            title: "Media Sent",
            message:
              count > 1
                ? `All ${count} files were sent successfully.`
                : "Your media has been sent successfully",
            icon: NotificationIcon.SUCCESS,
          })
        );
      }

      setShowPreview(false);
      setUploadedFiles([]);
      setCaption("");
      setUploadError(null);
      setSelectedPreviewIndex(0);
    } finally {
      setIsSendingBatch(false);
    }
  };

  const handleMediaScheduleFromMenu = (schedulePayload: SchedulePayload) => {
    handleSend(schedulePayload);
  };

  const renderMainPreview = (item: UploadedMediaItem) => {
    const fileType = item.file_type || '';

    if (fileType === "image") {
      return (
        <img
          className="max-h-[min(24rem,50vh)] w-auto max-w-full flex-none rounded-md shadow-lg object-contain"
          src={item.file_url}
          alt={item.original_name}
        />
      );
    }

    if (fileType === "video") {
      return (
        <video
          className="max-h-[min(24rem,50vh)] w-auto max-w-full flex-none rounded-md shadow-lg"
          controls
        >
          <source src={item.file_url} type={item.fileType} />
          Your browser does not support the video tag.
        </video>
      );
    }

    if (fileType === "audio") {
      return (
        <div className="flex w-full max-w-md flex-col items-center gap-3 rounded-lg border border-solid border-neutral-border bg-white px-6 py-6 shadow-sm">
          <SubframeCore.Icon
            className="text-heading-1 font-heading-1 text-brand-600"
            name="FeatherMic"
          />
          <audio
            className="w-full max-w-full"
            controls
            preload="metadata"
            src={item.file_url}
          >
            <source src={item.file_url} type={item.fileType || "audio/mpeg"} />
            Your browser does not support the audio element.
          </audio>
          <span className="text-caption font-caption text-subtext-color">{item.original_name}</span>
        </div>
      );
    }

    return (
      <div className="w-full min-h-[320px] max-h-[min(600px,55vh)]">
        <embed
          src={item.file_url}
          type={item.fileType}
          width="100%"
          height="100%"
          className="min-h-[320px] max-h-[min(600px,55vh)] rounded-md shadow-lg"
        />
      </div>
    );
  };

  const selectedItem = uploadedFiles[selectedPreviewIndex];
  const sendDisabled =
    isUploading ||
    isMediaUploading ||
    isSendingBatch ||
    uploadedFiles.length === 0 ||
    !caption.trim();

  return (
    <>
      <div className="flex w-full flex-col items-start rounded-md border border-solid border-neutral-border bg-default-background shadow-sm">
        <div className="flex w-full items-center gap-2 border-b border-solid border-neutral-border px-6 py-6">
          <span className="text-heading-3 font-heading-3 text-default-font">
            Upload files
          </span>
          <span className="text-body font-body text-subtext-color">
            Image, video, audio, or document (max {MAX_FILES})
          </span>
        </div>

        <div className="flex w-full flex-col items-start gap-4 px-6 py-6">
          <div
            className="flex w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed border-brand-600 px-6 py-6 cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <SubframeCore.Icon
              className="text-heading-1 font-heading-1 text-brand-700"
              name="FeatherUploadCloud"
            />
            <span className="text-body font-body text-default-font text-center">
              Click to select files or drag to upload (up to {MAX_FILES})
            </span>
            <span className="text-caption font-caption text-subtext-color text-center max-w-md px-2">
              On PC, in the file picker hold Ctrl (Windows) or ⌘ (Mac) to select multiple files.
            </span>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              multiple
              onChange={handleFileSelect}
              accept="image/*,video/*,audio/*,.mp3,.m4a,.aac,.wav,.ogg,.opus,.flac,.webm,.pdf,.doc,.docx,.xls,.xlsx"
              aria-label="Upload one or more files (use Ctrl or Command in the file dialog to select multiple)"
              title="Select multiple files with Ctrl+click (Windows) or ⌘+click (Mac)"
            />
          </div>

          {uploadedFiles.map((f) => (
            <div
              key={f.id}
              className="conveythis-no-translate flex w-full items-center gap-2 rounded-md bg-neutral-50 px-2 py-2"
            >
              <div className="flex items-center gap-4 px-1 py-1">
                <SubframeCore.Icon
                  className="text-body font-body text-brand-700"
                  name="FeatherCheckCircle"
                />
              </div>
              <span className="grow shrink-0 basis-0 text-caption-bold font-caption-bold text-default-font truncate">
                {f.original_name}
              </span>
              <IconButton
                size="small"
                icon="FeatherTrash"
                onClick={(e) => {
                  e.stopPropagation();
                  removeUploadedFile(f.id);
                }}
              />
            </div>
          ))}

          {isUploading && (
            <div className="flex w-full items-center gap-2 rounded-md bg-neutral-50 px-2 py-2">
              <div className="flex items-center gap-4 px-1 py-1">
                <SubframeCore.Icon
                  className="animate-spin text-body font-body text-brand-700"
                  name="FeatherLoader"
                />
              </div>
              <span className="grow shrink-0 basis-0 text-caption-bold font-caption-bold text-default-font">
                {uploadProgress
                  ? `Uploading... ${uploadProgress.done}/${uploadProgress.total}`
                  : 'Uploading...'}
              </span>
            </div>
          )}
        </div>
      </div>

      <DialogLayout open={showPreview} onOpenChange={setShowPreview}>
        <div className="flex h-full max-h-[90vh] w-full flex-col items-start gap-4 bg-default-background px-6 py-6 overflow-hidden">
          <div className="flex w-full items-center justify-between border-b border-solid border-neutral-border pb-4 shrink-0">
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1 min-w-0">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Media Preview
              </span>
              <span className="conveythis-no-translate text-body font-body text-subtext-color truncate w-full">
                {uploadedFiles.length} file{uploadedFiles.length !== 1 ? 's' : ''}
                {selectedItem
                  ? ` · ${selectedItem.original_name}${
                      selectedItem.size != null ? ` · ${formatFileSize(selectedItem.size)}` : ''
                    }`
                  : ''}
              </span>
            </div>
            <IconButton
              icon="FeatherX"
              onClick={() => setShowPreview(false)}
            />
          </div>

          {uploadedFiles.length > 1 && (
            <div className="flex w-full gap-2 overflow-x-auto pb-1 shrink-0 [scrollbar-width:thin]">
              {uploadedFiles.map((f, index) => {
                const isSel = index === selectedPreviewIndex;
                const ft = f.file_type || '';
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setSelectedPreviewIndex(index)}
                    className={`relative shrink-0 rounded-md border-2 overflow-hidden transition-colors ${
                      isSel ? 'border-brand-600 ring-2 ring-brand-200' : 'border-neutral-200 hover:border-neutral-300'
                    }`}
                    title={f.original_name}
                  >
                    {ft === 'image' ? (
                      <img
                        src={f.file_url}
                        alt=""
                        className="h-16 w-16 object-cover"
                      />
                    ) : ft === 'video' ? (
                      <div className="h-16 w-16 bg-neutral-800 flex items-center justify-center">
                        <SubframeCore.Icon
                          className="text-white text-xl"
                          name="FeatherVideo"
                        />
                      </div>
                    ) : ft === 'audio' ? (
                      <div className="h-16 w-16 bg-brand-700 flex items-center justify-center">
                        <SubframeCore.Icon
                          className="text-white text-xl"
                          name="FeatherMic"
                        />
                      </div>
                    ) : (
                      <div className="h-16 w-16 bg-neutral-100 flex flex-col items-center justify-center gap-0.5 px-1">
                        <SubframeCore.Icon
                          className="text-neutral-600"
                          name="FeatherFile"
                        />
                      </div>
                    )}
                    <span className="absolute bottom-0 left-0 right-0 bg-black/55 text-[10px] text-white text-center py-0.5 truncate px-0.5">
                      {index + 1}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          <div className="relative w-full flex-1 min-h-0 flex flex-col items-center justify-center gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-4 overflow-auto">
            {isUploading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto" />
                  <div className="mt-2 text-neutral-600">
                    {uploadProgress
                      ? `Uploading... ${uploadProgress.done}/${uploadProgress.total}`
                      : 'Uploading...'}
                  </div>
                </div>
              </div>
            ) : uploadError ? (
              <div className="text-center text-red-500 p-4">
                {uploadError}
              </div>
            ) : selectedItem ? (
              <div className="flex flex-col items-center w-full max-w-full">
                {renderMainPreview(selectedItem)}
              </div>
            ) : null}
          </div>

          <TextArea
            className="conveythis-no-translate h-auto w-full flex-none shrink-0"
            label="Caption (applied to all files)"
            helpText=""
          >
            <TextArea.Input
              className="conveythis-no-translate h-auto min-h-24 w-full flex-none"
              placeholder="Add a caption..."
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              disabled={isUploading}
            />
          </TextArea>
          <div className="flex w-full flex-col items-center gap-4 shrink-0">
            <div className="flex grow shrink-0 basis-0 items-center rounded-lg bg-brand-600">
              <SubframeCore.Tooltip.Provider>
                <SubframeCore.Tooltip.Root>
                  <SubframeCore.Tooltip.Trigger asChild={true}>
                    <IconButton
                      className="h-auto w-8 flex-none self-stretch"
                      variant="brand-primary"
                      icon="FeatherCalendarClock"
                      onClick={() => setIsScheduleMessagePopupOpen(true)}
                    />
                  </SubframeCore.Tooltip.Trigger>
                  <SubframeCore.Tooltip.Portal>
                    <SubframeCore.Tooltip.Content
                      side="bottom"
                      align="center"
                      sideOffset={4}
                      asChild={true}
                    >
                      <Tooltip>Schedule send</Tooltip>
                    </SubframeCore.Tooltip.Content>
                  </SubframeCore.Tooltip.Portal>
                </SubframeCore.Tooltip.Root>
              </SubframeCore.Tooltip.Provider>
              <div className="flex w-px flex-none flex-col items-center gap-2 self-stretch bg-brand-50" />
              <Button
                className="h-auto min-h-[36px] w-auto flex-none self-stretch"
                iconRight="FeatherSendHorizontal"
                onClick={() => handleSend()}
                disabled={sendDisabled}
                loading={isMediaUploading || isSendingBatch}
              >
                Send{uploadedFiles.length > 1 ? ` (${uploadedFiles.length})` : ''}
              </Button>
            </div>
          </div>
        </div>
      </DialogLayout>

      <ScheduleMessage
        open={isScheduleMessagePopupOpen}
        setIsScheduleMessagePopupOpen={setIsScheduleMessagePopupOpen}
        onScheduleSubmit={handleMediaScheduleFromMenu}
      />
    </>
  );
};

export default FileSelectionMenu;
